export type CareJourneyType = 'Questionnaire' | 'Lab' | 'Visit'
export type CareJourneyTypeSlug = 'questionnaires' | 'labs' | 'visits'

const TYPE_TO_SLUG: Record<CareJourneyType, CareJourneyTypeSlug> = {
    Questionnaire: 'questionnaires',
    Lab: 'labs',
    Visit: 'visits',
}

const SLUG_TO_TYPE: Record<CareJourneyTypeSlug, CareJourneyType> = {
    questionnaires: 'Questionnaire',
    labs: 'Lab',
    visits: 'Visit',
}

const SAFE_REQUIRED_ID = /^[A-Za-z0-9_-]+$/

export interface CareJourneyCriterionLike {
    requirement_id: string
    required_id: string
    required_type: string
    status: string
    deadline: string | null
}

export interface CareJourneyFlowEntry {
    id: string
    length: number
}

export interface CareJourneyFlowSnapshot {
    entries: CareJourneyFlowEntry[]
    entryGlobal: number
    pendingTotal: number
}

export function careJourneyTypeFromSlug(slug: string): CareJourneyType | null {
    return SLUG_TO_TYPE[slug as CareJourneyTypeSlug] ?? null
}

export function careJourneyTypeSlug(type: CareJourneyType): CareJourneyTypeSlug {
    return TYPE_TO_SLUG[type]
}

export function groupLandingHref(type: CareJourneyType): string {
    return `/care-journey/${careJourneyTypeSlug(type)}`
}

export function careJourneyQuestionHref(questionNumber = 1): string {
    return `${groupLandingHref('Questionnaire')}/question/${questionNumber}`
}

export function criterionLandingHref(type: CareJourneyType, requirementId: string | null | undefined): string | null {
    if (type !== 'Questionnaire' || !requirementId || !SAFE_REQUIRED_ID.test(requirementId)) return null
    return `${groupLandingHref(type)}/${encodeURIComponent(requirementId)}`
}

export function sortedPendingQuestionnaireCriteria<T extends CareJourneyCriterionLike>(criteria: readonly T[]): T[] {
    return [...criteria]
        .filter((criterion) => criterion.status === 'pending' && criterion.required_type === 'Questionnaire')
        .sort((a, b) => {
            if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline)
            if (a.deadline) return -1
            if (b.deadline) return 1
            return 0
        })
}

export function soonestPendingQuestionnaireDeadline<T extends CareJourneyCriterionLike>(
    criteria: readonly T[],
): string | null {
    return sortedPendingQuestionnaireCriteria(criteria)[0]?.deadline ?? null
}

export function buildCareJourneyFlow(
    criteria: readonly CareJourneyCriterionLike[],
    lengthsById: ReadonlyMap<string, number>,
): CareJourneyFlowEntry[] {
    return sortedPendingQuestionnaireCriteria(criteria).map((criterion) => ({
        id: criterion.required_id,
        length: lengthsById.get(criterion.required_id) ?? 0,
    }))
}

export function flowTotal(entries: readonly CareJourneyFlowEntry[]): number {
    return entries.reduce((sum, entry) => sum + entry.length, 0)
}

export function entryStartQuestion(entries: readonly CareJourneyFlowEntry[], entryIndex: number): number {
    return entries.slice(0, entryIndex).reduce((sum, entry) => sum + entry.length, 1)
}

export function firstPendingEntryStartQuestion(
    entries: readonly CareJourneyFlowEntry[],
    pendingIds: ReadonlySet<string>,
): number | null {
    const entryIndex = entries.findIndex((entry) => pendingIds.has(entry.id))
    return entryIndex === -1 ? null : entryStartQuestion(entries, entryIndex)
}

export function resolveCareJourneyQuestion(entries: readonly CareJourneyFlowEntry[], globalQuestion: number) {
    let offset = 0
    for (let index = 0; index < entries.length; index += 1) {
        const entry = entries[index]
        if (globalQuestion <= offset + entry.length) {
            return {
                entry,
                index,
                localQuestion: globalQuestion - offset,
                globalOffset: offset,
            }
        }
        offset += entry.length
    }
    return null
}

export function progressForQuestion(globalQuestion: number, total: number, snapshot: CareJourneyFlowSnapshot | null) {
    if (
        snapshot &&
        globalQuestion >= snapshot.entryGlobal &&
        globalQuestion < snapshot.entryGlobal + snapshot.pendingTotal
    ) {
        return {
            current: globalQuestion - snapshot.entryGlobal + 1,
            total: snapshot.pendingTotal,
        }
    }
    return { current: globalQuestion, total }
}
